import { logEvent } from "firebase/analytics";
import { getFirebaseAnalytics } from "@services/config/firebase";

/**
 * Ticketin Analytics — wraps Firebase Analytics logEvent with typed events.
 *
 * GA4 collects IP-based geolocation automatically (city, region, country).
 * View visitor maps in: GA4 → Reports → Demographics → Geographic.
 *
 * Funnel visible in: GA4 → Explore → Funnel exploration:
 *   view_item → begin_checkout → add_payment_info → purchase
 */

// ── Page tracking ──

/** Fire on every route change. GA4 uses this for page-level metrics. */
export function trackPageView(path: string, title: string) {
  const a = getFirebaseAnalytics();
  if (!a) return;
  logEvent(a, "page_view", { page_path: path, page_title: title });
}

// ── Event detail ──

/** User opens an event detail page. Powers "most viewed events" report. */
export function trackViewEvent(params: {
  eventId: string;
  eventName: string;
  category: string;
  price: number;
}) {
  const a = getFirebaseAnalytics();
  if (!a) return;
  logEvent(a, "view_item", {
    currency: "USD",
    value: params.price,
    items: [
      {
        item_id: params.eventId,
        item_name: params.eventName,
        item_category: params.category,
        price: params.price,
      },
    ],
  });
}

// ── Checkout funnel ──

/** Step 1 — User opens checkout modal (selects tickets). */
export function trackBeginCheckout(params: {
  eventId: string;
  eventName: string;
  value: number;
  locality: string;
  quantity: number;
}) {
  const a = getFirebaseAnalytics();
  if (!a) return;
  logEvent(a, "begin_checkout", {
    currency: "USD",
    value: params.value,
    items: [
      {
        item_id: params.eventId,
        item_name: params.eventName,
        item_category: params.locality,
        quantity: params.quantity,
        price: params.value / params.quantity,
      },
    ],
  });
}

/** Step 2 — User fills contact data and advances to payment selection. */
export function trackAddContactInfo(params: {
  eventId: string;
  eventName: string;
  value: number;
}) {
  const a = getFirebaseAnalytics();
  if (!a) return;
  logEvent(a, "add_shipping_info", {
    currency: "USD",
    value: params.value,
    items: [{ item_id: params.eventId, item_name: params.eventName }],
  });
}

/** Step 3 — User selects a payment method. */
export function trackAddPaymentInfo(params: {
  eventId: string;
  eventName: string;
  value: number;
  paymentMethod: string;
}) {
  const a = getFirebaseAnalytics();
  if (!a) return;
  logEvent(a, "add_payment_info", {
    currency: "USD",
    value: params.value,
    payment_type: params.paymentMethod,
    items: [{ item_id: params.eventId, item_name: params.eventName }],
  });
}

export interface PurchaseItem {
  localityId: string;
  localityName: string;
  price: number;
  comision: number;
  quantity: number;
}

/** Step 4 — Payment confirmed. Powers conversion + revenue reports. */
export function trackPurchase(params: {
  transactionId: string;
  eventId: string;
  eventName: string;
  value: number;
  paymentMethod: string;
  items?: PurchaseItem[];
}) {
  const a = getFirebaseAnalytics();
  if (!a) return;
  const gaItems =
    params.items && params.items.length > 0
      ? params.items.map((it) => ({
          item_id: it.localityId,
          item_name: it.localityName,
          item_category: params.eventName,
          price: it.price + it.comision,
          quantity: it.quantity,
        }))
      : [{ item_id: params.eventId, item_name: params.eventName }];
  logEvent(a, "purchase", {
    transaction_id: params.transactionId,
    currency: "USD",
    value: params.value,
    payment_type: params.paymentMethod,
    items: gaItems,
  });
}

/** User abandons checkout (closes modal before completing). */
export function trackCheckoutAbandoned(params: {
  eventId: string;
  eventName: string;
  stepReached: number;
}) {
  const a = getFirebaseAnalytics();
  if (!a) return;
  logEvent(a, "checkout_abandoned", {
    event_id: params.eventId,
    event_name: params.eventName,
    step_reached: params.stepReached,
  });
}

// ── Error tracking ──

/**
 * Report an application error to GA4.
 * Visible in: GA4 → Reports → Events → "app_error".
 * Use for: uncaught exceptions, Error Boundary catches, failed API calls.
 */
export function trackError(params: {
  /** Short error description or message */
  description: string;
  /** Component or module where the error occurred */
  component?: string;
  /** true = fatal (crashed the page), false = handled */
  fatal?: boolean;
}) {
  const a = getFirebaseAnalytics();
  if (!a) return;
  logEvent(a, "app_error", {
    description: params.description.slice(0, 100), // GA4 param limit
    component: params.component ?? "unknown",
    fatal: params.fatal ? 1 : 0,
  });
}
